import { HubConnectionState } from '@microsoft/signalr';
import { ComponentProps, useCallback, useRef, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { Stack } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useSearchV2DataRefetch } from 'src/hooks/useSearchV2DataRefetch';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Feature,
  SaleCancellationReason,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';

export type BulkDeallocateCancelSalesDialogProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  deallocateOnly: boolean;
  onOkay: (
    removeArtifacts: boolean | null,
    adminHold: boolean | null,
    keepAllottedTickets: boolean | null,
    cancellationReason: SaleCancellationReason | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel: () => void;
};

export function BulkDeallocateCancelSalesDialog({
  deallocateOnly,
  updateKey,
  onOkay,
  onCancel,
  ...rest
}: BulkDeallocateCancelSalesDialogProps) {
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { refreshData } = usePurchaseDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const { searchV2DataDelayedRefetch } = useSearchV2DataRefetch();
  const { loginContext } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);

  const [removeArtifacts, setRemoveArtifacts] = useState<boolean | null>(null);
  const [adminHold, setAdminHold] = useState<boolean | null>(
    // Prefill according to account setting but allow user to override
    loginContext?.user?.activeAccount?.sellerAccountSettings
      ?.adminHoldIndefinitelyForCancelledSales ?? null
  );
  const [keepAllocatedTickets, setKeepAllocatedTickets] = useState<
    boolean | null
  >(null);
  const [cancellationReason, setCancellationReason] =
    useState<SaleCancellationReason | null>(null);

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await searchV2DataDelayedRefetch();

        // Depending whether it's Listing, Sales, or Purchase
        // the context would be different here
        if (refreshData) {
          await refreshData(true);
        }
        if (refreshExpandedListItems) {
          await refreshExpandedListItems();
        }
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }

        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshData,
      refreshExpandedListItems,
      searchV2DataDelayedRefetch,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        onClose(doneProgress, false);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob, stage } = useBulkEditHub(
    ActionOutboxEntityType.Sale,
    BulkActionType.CancelSale,
    updateKey,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      if (onPreviewReceived) {
        onOkay(null, null, null, null, false, onPreviewReceived);
      } else {
        onOkay(
          removeArtifacts,
          adminHold,
          keepAllocatedTickets,
          cancellationReason,
          hasBackgroundBulkEditFeature,
          undefined,
          preview
        );
      }
      if (!hasBackgroundBulkEditFeature) {
        setIsLoading(false);
      }
    },
    [
      onOkay,
      removeArtifacts,
      adminHold,
      keepAllocatedTickets,
      cancellationReason,
      hasBackgroundBulkEditFeature,
      preview,
    ]
  );

  const submittButtonRef = useRef<HTMLButtonElement>(null);

  const hasKeepAllocatedTicketsFeature = useUserHasFeature(
    Feature.CancelSaleKeepTickets
  );

  return (
    <GenericDialog
      {...rest}
      size={stage === BulkEditStage.Preview ? 'xl' : 'md'}
      header={
        <BulkEditHeader
          headerText={
            <Content
              id={
                deallocateOnly
                  ? ContentId.DeallocateSales
                  : ContentId.RejectSales
              }
            />
          }
        />
      }
      onOpened={() => {
        setRemoveArtifacts(null);
        setAdminHold(null);
        setKeepAllocatedTickets(null);
        setCancellationReason(null);
        setIsLoading(false);
        initJob();
      }}
      onKeyUp={(e) => {
        if (submittButtonRef.current && e.key === 'Enter' && !isLoading) {
          submittButtonRef.current.click();
        }
      }}
      onClick={(e) => {
        // This so that this dialog does not cause event to flow to the EventHeader open/close event
        e.stopPropagation();
        e.preventDefault();
      }}
      onClosed={() => {
        setMainDialogOpened(false);
        if (progress) {
          setSelectionMode(undefined);
        }
        // Call the outside one if there is one
        rest.onClosed?.();
      }}
      footer={
        isRefreshing ? null : (
          <BulkEditFooter
          entityType={ActionOutboxEntityType.Sale}
          isLoading={isLoading}
          hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
          onClose={onClose}
          onSubmit={onSubmitHandler}
          submittButtonRef={submittButtonRef}
        />
        )
      }
      onCancel={isLoading ? undefined : onCancel}
    >
      <BulkEditStatus
        entityType={ActionOutboxEntityType.Sale}
        isLoading={isRefreshing || isLoading}
        updateKey={updateKey}
      >
        <Stack direction="column" width="full" gap="xl">
          <b>
            <Content
              id={
                deallocateOnly
                  ? ContentId.BulkDeallocateSalesInformation
                  : ContentId.BulkRejectSalesInformation
              }
            />
          </b>

          <Stack direction="column" gap="l">
            <span>
              <Content id={ContentId.ResetFulfillmentRemoveArtifactsOptions} />
            </span>
            <Checkbox
              checked={removeArtifacts == true}
              onChange={() => setRemoveArtifacts((prev) => !prev)}
              label={<Content id={ContentId.RemovePredeliveredArtifacts} />}
              labelPosition="right"
            />
          </Stack>

          <Stack direction="column" gap="l">
            <span>
              <Content id={ContentId.DeallocateSaleListingOnHoldOptions} />
            </span>
            <Checkbox
              checked={adminHold == true}
              onChange={() => setAdminHold((prev) => !prev)}
              label={<Content id={ContentId.PutSaleListingOnHold} />}
              labelPosition="right"
            />
          </Stack>

          {hasKeepAllocatedTicketsFeature && !deallocateOnly && (
            <Stack direction="column" gap="l">
              <span>
                <Content id={ContentId.CancelSaleKeepTicketOptions} />
              </span>
              <Checkbox
                checked={keepAllocatedTickets == true}
                onChange={() => setKeepAllocatedTickets((prev) => !prev)}
                label={<Content id={ContentId.KeepAllocatedTickets} />}
                labelPosition="right"
              />
            </Stack>
          )}
        </Stack>
      </BulkEditStatus>
    </GenericDialog>
  );
}
